import { useEffect } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import Icon from '../../components/forms/icon.component';
import ManualShell from './manual.shell';
import Block from './manual.renderer';
import { MANUAL_SECTIONS, findSection } from './content';

export default function ManualSectionPage() {
  const { slug } = useParams<{ slug: string }>();
  const { hash } = useLocation();
  const section = findSection(slug);

  // Salta al tema del ancla (#id) cuando se entra desde el buscador o un enlace.
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0 });
      return;
    }
    const el = document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash, slug]);

  if (!section) return <Navigate to="/manual" replace />;

  const idx = MANUAL_SECTIONS.findIndex((s) => s.slug === section.slug);
  const prev = idx > 0 ? MANUAL_SECTIONS[idx - 1] : undefined;
  const next = idx < MANUAL_SECTIONS.length - 1 ? MANUAL_SECTIONS[idx + 1] : undefined;

  return (
    <ManualShell>
      <div className="mb-6">
        <Link to="/manual" className="text-muted fs-8 text-decoration-none">
          Manual
        </Link>
        <h1 className="fw-bolder fs-2x mb-1 mt-1">{section.title}</h1>
        <div className="text-muted fs-7">{section.summary}</div>
      </div>

      {/* Índice de la sección */}
      {section.topics.length > 1 && (
        <div className="card shadow-sm border-0 rounded-4 mb-6 manual-toc">
          <div className="card-body p-5">
            <div className="fw-bold text-muted text-uppercase fs-8 mb-3">En esta página</div>
            <div className="row g-2">
              {section.topics.map((t, i) => (
                <div key={t.id} className="col-md-6">
                  <a href={`#${t.id}`} className="d-flex align-items-start text-gray-800 fs-7">
                    <span className="text-muted me-2">{i + 1}.</span>
                    <span>{t.title}</span>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Temas */}
      {section.topics.map((topic) => (
        <div
          key={topic.id}
          id={topic.id}
          className="card shadow-sm border-0 rounded-4 mb-6 manual-topic"
        >
          <div className="card-body p-5 p-lg-8">
            <h2 className="fw-bolder fs-2 text-gray-900 mb-5">
              {topic.title}
              <a
                href={`#${topic.id}`}
                className="manual-anchor text-muted fs-4"
                title="Enlace a este tema"
              >
                #
              </a>
            </h2>
            {topic.blocks.map((b, i) => (
              <Block key={i} block={b} />
            ))}
          </div>
        </div>
      ))}

      {/* Navegación entre secciones */}
      <div className="d-flex justify-content-between align-items-stretch gap-4 mb-10">
        {prev ? (
          <Link
            to={`/manual/${prev.slug}`}
            className="card shadow-sm border-0 rounded-4 flex-grow-1 text-decoration-none"
          >
            <div className="card-body p-4 d-flex align-items-center">
              <Icon icon="ki-left" size="fs-3" className="me-3 text-muted" />
              <div>
                <div className="text-muted fs-8">Anterior</div>
                <div className="fw-bold fs-7 text-gray-900">{prev.title}</div>
              </div>
            </div>
          </Link>
        ) : (
          <div className="flex-grow-1" />
        )}
        {next ? (
          <Link
            to={`/manual/${next.slug}`}
            className="card shadow-sm border-0 rounded-4 flex-grow-1 text-decoration-none"
          >
            <div className="card-body p-4 d-flex align-items-center justify-content-end text-end">
              <div>
                <div className="text-muted fs-8">Siguiente</div>
                <div className="fw-bold fs-7 text-gray-900">{next.title}</div>
              </div>
              <Icon icon="ki-right" size="fs-3" className="ms-3 text-muted" />
            </div>
          </Link>
        ) : (
          <div className="flex-grow-1" />
        )}
      </div>
    </ManualShell>
  );
}
